import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import TopicCard from '@/components/feature/TopicCard';
import { useSeo } from '@/hooks/useSeo';
import { SITE } from '@/constants/site';
import { topicCategories, topics } from '@/mocks/topics';

type SortKey = 'heat' | 'comments' | 'latest';

const sortOptions: { key: SortKey; label: string }[] = [
  { key: 'heat', label: '按热度' },
  { key: 'comments', label: '按讨论数' },
  { key: 'latest', label: '按最新' },
];

export default function Topics() {
  const [params, setParams] = useSearchParams();
  const [keyword, setKeyword] = useState(params.get('q') ?? '');
  const [category, setCategory] = useState(params.get('category') ?? 'all');
  const [sort, setSort] = useState<SortKey>('heat');
  const [visible, setVisible] = useState(9);

  useEffect(() => {
    setKeyword(params.get('q') ?? '');
    setCategory(params.get('category') ?? 'all');
    setVisible(9);
  }, [params]);

  useSeo({
    title: '话题广场 - 分类浏览全部热议话题',
    description:
      '话题广场汇集综艺现场、影视剧集、选秀偶像、生活圈、品牌商业与体育赛场等领域的全部热议话题，支持关键词搜索、分类筛选与热度排序。',
    keywords: '话题广场,热议话题,话题分类,关键词搜索,综艺现场,影视剧集,选秀偶像',
    path: '/topics',
    type: 'website',
    jsonLd: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'CollectionPage',
          name: '话题广场',
          description: '分类浏览全部热议话题，支持搜索与排序。',
          inLanguage: 'zh-CN',
        },
        {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}/` },
            { '@type': 'ListItem', position: 2, name: '话题广场', item: `${window.location.origin}/topics` },
          ],
        },
        {
          '@type': 'ItemList',
          name: '热议话题列表',
          itemListElement: topics.slice(0, 10).map((topic, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            name: topic.title,
            url: `${window.location.origin}/topic/${topic.id}`,
          })),
        },
      ],
    },
  });

  const filtered = useMemo(() => {
    const key = keyword.trim().toLowerCase();
    const list = topics.filter((topic) => {
      const matchCategory = category === 'all' || topic.categoryId === category;
      const matchKeyword =
        !key ||
        topic.title.toLowerCase().includes(key) ||
        topic.summary.toLowerCase().includes(key) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(key));
      return matchCategory && matchKeyword;
    });

    return [...list].sort((a, b) => {
      if (sort === 'comments') return b.comments - a.comments;
      if (sort === 'latest') return a.id - b.id;
      return b.heat - a.heat;
    });
  }, [keyword, category, sort]);

  const updateUrl = (next: { q?: string; category?: string }) => {
    const merged = new URLSearchParams(params);
    if (next.q !== undefined) {
      if (next.q) merged.set('q', next.q);
      else merged.delete('q');
    }
    if (next.category !== undefined) {
      if (next.category && next.category !== 'all') merged.set('category', next.category);
      else merged.delete('category');
    }
    setParams(merged, { replace: true });
  };

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    updateUrl({ q: keyword.trim() });
  };

  const pickCategory = (id: string) => {
    setCategory(id);
    updateUrl({ category: id });
  };

  const shown = filtered.slice(0, visible);

  return (
    <SiteLayout>
      <section className="border-b border-background-200 bg-background-100">
        <div className="w-full px-4 py-10 md:px-8 md:py-12">
          <Breadcrumb items={[{ label: '话题广场' }]} />
          <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
            话题广场
          </h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            这里汇集了全部正在被讨论的话题。按领域筛选，或者直接搜索关键词，找到你想了解的那一条。
          </p>
        </div>
      </section>

      <section className="border-b border-background-200 bg-background-50">
        <div className="w-full px-4 py-8 md:px-8">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <form onSubmit={submitSearch} className="flex w-full items-center gap-2 lg:max-w-sm">
              <div className="flex flex-1 items-center gap-2 border border-background-300 bg-background-100 px-3">
                <i className="ri-search-line text-foreground-500" aria-hidden="true" />
                <input
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  aria-label="搜索话题"
                  placeholder="搜索话题或标签"
                  className="h-10 w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none"
                />
              </div>
              <button
                type="submit"
                className="h-10 whitespace-nowrap bg-primary-500 px-4 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                搜索
              </button>
            </form>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">排序</span>
              <div className="flex items-center gap-1 border border-background-200 bg-background-100 p-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => setSort(option.key)}
                    className={`cursor-pointer rounded-full px-3 py-1 text-xs transition-colors ${
                      sort === option.key
                        ? 'bg-primary-500 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-950'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => pickCategory('all')}
              className={`cursor-pointer border px-3 py-1.5 text-xs transition-colors ${
                category === 'all'
                  ? 'border-primary-500 bg-primary-500 text-background-50'
                  : 'border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
              }`}
            >
              全部
            </button>
            {topicCategories.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => pickCategory(item.id)}
                className={`flex cursor-pointer items-center gap-1.5 border px-3 py-1.5 text-xs transition-colors ${
                  category === item.id
                    ? 'border-primary-500 bg-primary-500 text-background-50'
                    : 'border-background-300 text-foreground-600 hover:border-primary-300 hover:text-primary-600'
                }`}
              >
                <i className={item.icon} aria-hidden="true" />
                {item.name}
              </button>
            ))}
          </div>

          <p className="mt-5 text-xs text-foreground-500">
            共找到 <span className="font-medium text-primary-600">{filtered.length}</span> 个话题
            {keyword.trim() && <span> · 关键词「{keyword.trim()}」</span>}
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="w-full px-4 py-12 md:px-8">
          {shown.length > 0 ? (
            <>
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {shown.map((topic, index) => (
                  <TopicCard key={topic.id} topic={topic} index={sort === 'heat' ? index : undefined} />
                ))}
              </div>
              {visible < filtered.length && (
                <div className="mt-10 text-center">
                  <button
                    type="button"
                    onClick={() => setVisible((v) => v + 6)}
                    className="whitespace-nowrap border border-background-300 bg-background-100 px-6 py-3 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-600"
                  >
                    加载更多话题 <i className="ri-arrow-down-line" aria-hidden="true" />
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="border border-dashed border-background-300 bg-background-100 py-20 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl" aria-hidden="true" />
              </span>
              <p className="mt-4 font-heading text-lg font-bold text-foreground-900">没有找到相关话题</p>
              <p className="mt-2 text-sm text-foreground-500">换个关键词，或者切换一下分类试试。</p>
              <button
                type="button"
                onClick={() => {
                  setKeyword('');
                  pickCategory('all');
                }}
                className="mt-5 whitespace-nowrap bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="border-t border-background-200 bg-background-100">
        <div className="w-full px-4 py-10 md:px-8">
          <p className="text-sm text-foreground-600">
            没找到想看的？<a href={`mailto:${SITE.email}`} className="text-primary-600 underline underline-offset-4">给我们投稿线索</a>，核实之后我们会整理成话题。
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}